import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText,
  Download,
  Search,
  Plus,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

export default function ComplianceReportsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock compliance reports data
  const reports = [
    {
      id: 1,
      reportNumber: 'CR-2024-001',
      reportType: 'Monthly State Report',
      operator: 'Green Valley Farms',
      state: 'California',
      submissionDate: '2024-12-01',
      dueDate: '2024-12-05',
      status: 'Submitted',
      reviewer: 'Jane Smith',
      findings: 'No issues found',
      description: 'November monthly compliance report for cultivation operations'
    },
    {
      id: 2,
      reportNumber: 'CR-2024-002',
      reportType: 'Inventory Audit',
      operator: 'Springfield Wellness',
      state: 'Colorado',
      submissionDate: null,
      dueDate: '2024-12-15',
      status: 'Pending',
      reviewer: null,
      findings: null,
      description: 'Quarterly inventory reconciliation audit'
    },
    {
      id: 3,
      reportNumber: 'CR-2024-003',
      reportType: 'Safety Inspection',
      operator: 'Pure Extracts LLC',
      state: 'Oregon',
      submissionDate: '2024-11-28',
      dueDate: '2024-11-30',
      status: 'Under Review',
      reviewer: 'Mike Johnson',
      findings: 'Minor issues identified',
      description: 'Annual facility safety and compliance inspection'
    },
    {
      id: 4,
      reportNumber: 'CR-2024-004',
      reportType: 'Financial Report',
      operator: 'CannaTest Analytics',
      state: 'Washington',
      submissionDate: '2024-12-03',
      dueDate: '2024-12-10',
      status: 'Approved',
      reviewer: 'Sarah Davis',
      findings: 'All requirements met',
      description: 'Q4 financial compliance report'
    },
    {
      id: 5,
      reportNumber: 'CR-2024-005',
      reportType: 'Monthly State Report',
      operator: 'Urban Leaf Collective',
      state: 'Nevada',
      submissionDate: null,
      dueDate: '2024-11-25',
      status: 'Overdue',
      reviewer: null,
      findings: null,
      description: 'October monthly compliance report - OVERDUE'
    },
    {
      id: 6,
      reportNumber: 'CR-2024-006',
      reportType: 'Waste Disposal Report',
      operator: 'Mountain View Grow',
      state: 'California',
      submissionDate: '2024-12-02',
      dueDate: '2024-12-08',
      status: 'Submitted',
      reviewer: 'Jane Smith',
      findings: 'Pending review',
      description: 'November waste disposal and destruction log'
    },
    {
      id: 7,
      reportNumber: 'CR-2024-007',
      reportType: 'Employee Training',
      operator: 'Quick Green Delivery',
      state: 'California',
      submissionDate: null,
      dueDate: '2024-12-20',
      status: 'Pending',
      reviewer: null,
      findings: null,
      description: 'Mandatory quarterly staff training certification'
    },
    {
      id: 8,
      reportNumber: 'CR-2024-008',
      reportType: 'Security Audit',
      operator: 'Edible Delights Co',
      state: 'Colorado',
      submissionDate: '2024-11-15',
      dueDate: '2024-11-20',
      status: 'Approved',
      reviewer: 'Mike Johnson',
      findings: 'All requirements met',
      description: 'Semi-annual security systems and procedures audit'
    }
  ];

  const filteredReports = reports.filter(report => {
    const matchesSearch = 
      report.reportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.reportType.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.operator.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || report.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Submitted':
        return <Badge className="bg-blue-500 text-white flex items-center gap-1"><Clock className="h-3 w-3" />Submitted</Badge>;
      case 'Under Review':
        return <Badge className="bg-purple-500 text-white flex items-center gap-1"><FileText className="h-3 w-3" />Under Review</Badge>;
      case 'Approved':
        return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />Approved</Badge>;
      case 'Pending':
        return <Badge className="bg-yellow-500 text-white flex items-center gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case 'Overdue':
        return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="h-3 w-3" />Overdue</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const summaryCards = [
    {
      title: 'Total Reports',
      value: reports.length,
      icon: FileText,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Approved',
      value: reports.filter(r => r.status === 'Approved').length,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Pending Review',
      value: reports.filter(r => r.status === 'Under Review' || r.status === 'Submitted').length,
      icon: Clock,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: 'Overdue',
      value: reports.filter(r => r.status === 'Overdue').length,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
  ];

  const viewReportDetails = (report) => {
    setSelectedReport(report);
    setIsDialogOpen(true);
  };

  const handleExport = () => {
    toast.success('Exporting compliance reports to Excel...');
  };

  const handleDownloadReport = (reportNumber) => {
    toast.success(`Downloading report ${reportNumber}...`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Compliance Reports</h1>
            <p className="text-muted-foreground mt-1">Audit trails and state compliance reports</p>
          </div>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Report
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Reports Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Compliance Reports</CardTitle>
                <CardDescription>View and manage compliance documentation</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by report number, type, or operator..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="under review">Under Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Report Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Submission Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReports.length > 0 ? (
                    filteredReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">{report.reportNumber}</TableCell>
                        <TableCell>{report.reportType}</TableCell>
                        <TableCell>{report.operator}</TableCell>
                        <TableCell>{report.state}</TableCell>
                        <TableCell>{report.submissionDate || '-'}</TableCell>
                        <TableCell>{report.dueDate}</TableCell>
                        <TableCell>{getStatusBadge(report.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewReportDetails(report)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadReport(report.reportNumber)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        No reports found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Report Details</DialogTitle>
            <DialogDescription>Complete information about the compliance report</DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Report Number</p>
                  <p className="font-medium">{selectedReport.reportNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Report Type</p>
                  <p className="font-medium">{selectedReport.reportType}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Operator</p>
                  <p className="font-medium">{selectedReport.operator}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">State</p>
                  <p className="font-medium">{selectedReport.state}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Submission Date</p>
                  <p className="font-medium">{selectedReport.submissionDate || 'Not submitted'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Due Date</p>
                  <p className="font-medium">{selectedReport.dueDate}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedReport.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Reviewer</p>
                  <p className="font-medium">{selectedReport.reviewer || 'Not assigned'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Description</p>
                <p className="text-sm p-3 bg-muted rounded-lg">{selectedReport.description}</p>
              </div>

              {selectedReport.findings && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Findings</p>
                  <p className="text-sm p-3 bg-muted rounded-lg">{selectedReport.findings}</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => handleDownloadReport(selectedReport.reportNumber)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Report
                </Button>
                <Button variant="outline" className="flex-1">Edit Report</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
